import React from 'react';
import { Helmet } from 'react-helmet-async';
import SEO_CONFIG from '@/config/seo';

const defaultImage = SEO_CONFIG.defaultImage.startsWith('http')
  ? SEO_CONFIG.defaultImage
  : `${SEO_CONFIG.baseUrl}${SEO_CONFIG.defaultImage.startsWith('/') ? SEO_CONFIG.defaultImage : '/' + SEO_CONFIG.defaultImage}`;

/**
 * SEO component – sets document title, meta description, OG, Twitter, canonical, JSON-LD.
 */
export default function SEO({
  title,
  description,
  canonical,
  image = defaultImage,
  noindex = false,
  jsonLd = null,
  keywords = null,
}) {
  const t = title ? `${title} | ${SEO_CONFIG.siteName}` : SEO_CONFIG.defaultTitle;
  const d = description || SEO_CONFIG.defaultDescription;
  const c = canonical
    ? canonical.startsWith('http')
      ? canonical
      : `${SEO_CONFIG.baseUrl}${canonical.startsWith('/') ? '' : '/'}${canonical}`
    : typeof window !== 'undefined'
      ? window.location.href
      : SEO_CONFIG.baseUrl;
  const img = image.startsWith('http') ? image : `${SEO_CONFIG.baseUrl}${image.startsWith('/') ? '' : '/'}${image}`;
  const kw = keywords || SEO_CONFIG.keywords;

  return (
    <Helmet>
      <title>{t}</title>
      <meta name="description" content={d} />
      <meta name="keywords" content={kw} />
      {noindex ? (
        <>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </>
      ) : (
        <>
          <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
          <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        </>
      )}
      <link rel="canonical" href={c} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={c} />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content={SEO_CONFIG.siteName} />
      <meta property="og:locale" content={SEO_CONFIG.locale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={c} />
      <meta name="twitter:title" content={t} />
      <meta name="twitter:description" content={d} />
      <meta name="twitter:image" content={img} />
      {SEO_CONFIG.twitterHandle && <meta name="twitter:site" content={SEO_CONFIG.twitterHandle} />}

      {jsonLd &&
        (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((ld, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(ld)}
          </script>
        ))}
    </Helmet>
  );
}
